import {
  Drop,
  SunDim,
  SunHorizon,
  Thermometer,
  Wind,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';
import type { WeatherHour } from './WeatherCard';
import { uiText } from '../i18n';

export interface WeatherDay {
  /** YYYY-MM-DD */
  date: string;
  /** "Heute", "Morgen", "Sa" */
  label: string;
  icon: PhosphorIcon;
  summary: string;
  min: number;
  max: number;
  /** Highest chance of rain that day, 0–100. */
  rain: number;
}

export interface WeatherDetailsProps {
  temp: number;
  icon: PhosphorIcon;
  summary: string;
  hint?: string;
  feelsLike?: number;
  /** km/h */
  wind?: number;
  uv?: number;
  /** "HH:MM" */
  sunrise?: string;
  sunset?: string;
  /** The next 24 hours, every three hours (eight columns fit a phone). */
  hours: WeatherHour[];
  days: WeatherDay[];
}

const uvLabel = (uv: number) => {
  const l = uiText().uvLevel;
  return uv <= 2 ? l.low : uv <= 5 ? l.moderate : uv <= 7 ? l.high : l.veryHigh;
};
const windLabel = (kmh: number) => {
  const l = uiText().windLevel;
  return kmh < 12 ? l.light : kmh < 29 ? l.moderate : kmh < 50 ? l.fresh : l.stormy;
};

/**
 * The weather "antippen" view: now, a few facts as tiles, a 24-hour curve with rain bars and
 * seven days as temperature ranges on one shared scale – readable at a glance, like a picture.
 * All numbers are real text for screen readers; curves and bars are decorative.
 */
export function WeatherDetails({
  temp,
  icon,
  summary,
  hint,
  feelsLike,
  wind,
  uv,
  sunrise,
  sunset,
  hours,
  days,
}: WeatherDetailsProps) {
  const maxRain = Math.max(0, ...hours.map((h) => h.rain));
  return (
    <div className="@container flex flex-col gap-section">
      <div className="flex items-center gap-gap-md">
        <Icon icon={icon} size="xl" weight="duotone" className="text-accent-tertiary" />
        <div className="flex flex-col">
          <span className="font-display text-4xl font-black leading-none tabular-nums">
            {temp}°
          </span>
          <span className="text-text-secondary">{summary}</span>
        </div>
        {hint && (
          <span className="ml-auto rounded-chip bg-accent-secondary-soft px-inset-sm py-1 text-sm font-bold">
            {hint}
          </span>
        )}
      </div>

      <ul
        className="grid grid-cols-2 gap-gap-sm @narrow:grid-cols-4 @medium:grid-cols-6"
        aria-label={uiText().today}
      >
        {feelsLike !== undefined && (
          <Fact icon={Thermometer} label={uiText().feelsLike} value={`${feelsLike}°`} />
        )}
        <Fact
          icon={Drop}
          label={uiText().rainRisk}
          value={`${maxRain} %`}
          note={uiText().next24Short}
        />
        {wind !== undefined && (
          <Fact icon={Wind} label={uiText().wind} value={`${wind} km/h`} note={windLabel(wind)} />
        )}
        {uv !== undefined && (
          <Fact icon={SunDim} label={uiText().uv} value={String(uv)} note={uvLabel(uv)} />
        )}
        {sunrise && sunset && (
          <Fact
            icon={SunHorizon}
            label={uiText().sun}
            value={`${sunrise} – ${sunset}`}
            className="col-span-2 @narrow:col-span-1 @medium:col-span-2"
          />
        )}
      </ul>

      {/* side by side on wide screens, so the whole forecast fits without scrolling */}
      <div className="grid grid-cols-1 gap-section @medium:grid-cols-2 @medium:gap-gap-xl">
        {hours.length > 1 && (
          <section aria-label={uiText().next24} className="flex flex-col gap-gap-sm">
            <h3 className="font-display text-lg font-black">{uiText().next24}</h3>
            <HourCurve hours={hours} />
          </section>
        )}

        {days.length > 0 && (
          <section aria-label={uiText().days7} className="flex flex-col gap-gap-sm">
            <h3 className="font-display text-lg font-black">{uiText().days7}</h3>
            <DayRanges days={days} />
          </section>
        )}
      </div>
    </div>
  );
}

function Fact({
  icon,
  label,
  value,
  note,
  className,
}: {
  icon: PhosphorIcon;
  label: string;
  value: string;
  note?: string;
  className?: string;
}) {
  return (
    <li
      className={cn('flex flex-col gap-1 rounded-control bg-surface-sunken p-inset-xs', className)}
    >
      <span className="flex items-center gap-gap-xs text-xs font-semibold text-text-secondary">
        <Icon icon={icon} size="sm" /> {label}
      </span>
      <span className="font-display text-xl font-black tabular-nums">{value}</span>
      {note && <span className="text-xs text-text-muted">{note}</span>}
    </li>
  );
}

/** Temperature line over rain bars, one column per three hours. */
function HourCurve({ hours }: { hours: WeatherHour[] }) {
  const temps = hours.map((h) => h.temp);
  const min = Math.min(...temps) - 1;
  const max = Math.max(...temps) + 1;
  const n = hours.length;
  const x = (i: number) => ((i + 0.5) / n) * 100;
  const y = (t: number) => 28 - ((t - min) / (max - min || 1)) * 24;
  const line = hours.map((h, i) => `${x(i)},${y(h.temp)}`).join(' ');
  const area = `${x(0)},30 ${line} ${x(n - 1)},30`;
  return (
    <div className="relative">
      <svg
        viewBox="0 0 100 30"
        preserveAspectRatio="none"
        aria-hidden
        className="h-16 w-full overflow-visible"
      >
        <polygon points={area} className="fill-accent-tertiary-soft" />
        <polyline
          points={line}
          fill="none"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
          className="stroke-reward-points"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <ol className="grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        {hours.map((h) => (
          <li key={h.time} className="flex flex-col items-center gap-1 text-center">
            <span className="text-sm font-bold tabular-nums">{h.temp}°</span>
            <Icon icon={h.icon} size="md" className="text-text-secondary" />
            <span
              aria-hidden
              className="flex h-8 w-2.5 items-end overflow-hidden rounded-chip bg-surface-sunken"
            >
              <span
                className="w-full rounded-chip bg-accent-secondary"
                style={{ height: `${h.rain}%` }}
              />
            </span>
            <span className="text-xs text-text-muted tabular-nums">{h.rain} %</span>
            {/* hour only ("15"): full times do not fit eight columns on a phone */}
            <span className="text-xs font-semibold text-text-secondary tabular-nums">
              <span aria-hidden>{h.time.slice(0, 2)}</span>
              <span className="sr-only">{uiText().hour(h.time)}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Seven days on one temperature scale: each bar spans that day's low to high. */
function DayRanges({ days }: { days: WeatherDay[] }) {
  const lo = Math.min(...days.map((d) => d.min));
  const hi = Math.max(...days.map((d) => d.max));
  const pos = (t: number) => ((t - lo) / (hi - lo || 1)) * 100;
  return (
    <ol className="flex flex-col">
      {days.map((d) => (
        <li
          key={d.date}
          className="flex items-center gap-gap-sm border-b border-border-subtle py-gap-xs last:border-b-0"
        >
          <span className="w-16 shrink-0 font-semibold">{d.label}</span>
          <Icon icon={d.icon} size="md" className="shrink-0 text-text-secondary" />
          <span className="flex w-14 shrink-0 items-center gap-1 whitespace-nowrap text-xs text-text-secondary tabular-nums">
            <Icon icon={Drop} size="sm" className="shrink-0" />
            <span>{d.rain}&nbsp;%</span>
            <span className="sr-only">{uiText().rainRiskOf(d.summary)}</span>
          </span>
          <span className="w-8 shrink-0 text-right text-sm text-text-secondary tabular-nums">
            <span className="sr-only">{uiText().low}</span>
            {d.min}°
          </span>
          <span aria-hidden className="relative h-2 flex-1 rounded-chip bg-surface-sunken">
            <span
              className="absolute inset-y-0 rounded-chip bg-accent-tertiary"
              style={{ left: `${pos(d.min)}%`, width: `${Math.max(4, pos(d.max) - pos(d.min))}%` }}
            />
          </span>
          <span className="w-8 shrink-0 text-sm font-bold tabular-nums">
            <span className="sr-only">{uiText().high}</span>
            {d.max}°
          </span>
        </li>
      ))}
    </ol>
  );
}
